"use client"

import { useEffect, useState } from "react"
import { MaintenanceScreen } from "@/components/maintenance-screen"
import { LOCALE_COOKIE } from "@/i18n/locales"
import fallback from "@/lib/maintenance-fallback.json"

type FallbackLocale = keyof typeof fallback

// Last line of defence: replaces the whole root layout when the layout itself
// crashes, so there is no NextIntlClientProvider here and useTranslations()
// would throw. The copy comes from lib/maintenance-fallback.json instead: a tiny
// bundled copy of the `maintenanceScreen` messages (scripts/i18n-check.mjs
// fails if it drifts from messages/*.json). The language is read straight from
// the locale cookie, then the browser, and is English until that runs.
function detectLocale(): FallbackLocale {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`))
    const saved = match ? decodeURIComponent(match[1]) : ""
    if (Object.prototype.hasOwnProperty.call(fallback, saved)) return saved as FallbackLocale
    const browser = (navigator.language || "").slice(0, 2).toLowerCase()
    if (Object.prototype.hasOwnProperty.call(fallback, browser)) return browser as FallbackLocale
  } catch {}
  return "en"
}

export default function GlobalError({ error, reset }: { error: Error & { digest?: string }; reset: () => void }) {
  const [locale, setLocale] = useState<FallbackLocale>("en")

  useEffect(() => {
    console.error("[global-error]", error)
  }, [error])

  useEffect(() => {
    setLocale(detectLocale())
  }, [])

  const t = fallback[locale]

  return (
    <html lang={locale}>
      <body style={{ margin: 0, fontFamily: "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif" }}>
        <MaintenanceScreen
          title={t.title}
          body={t.body}
          retryLabel={t.tryAgain}
          homeLabel={t.backToHome}
          helpLabel={t.stillStuck}
          referenceLabel={t.reference}
          digest={error.digest}
          onRetry={reset}
        />
      </body>
    </html>
  )
}
